import { useCallback, useEffect, useMemo, useRef, useState, type FormEvent } from "react";
import { ArrowRightIcon, MagnifyingGlassIcon } from "@radix-ui/react-icons";
import { KeyboardInput, useKeyboard, type FlowControls, type FlowScreen } from "../h5-runtime";
import { ProductSearchApiError } from "../api/client";
import type { WishlistBatch, WishlistItem } from "./contracts";
import { WishlistCandidatePanel } from "./WishlistCandidatePanel";
import { WishlistItemRow } from "./WishlistItemRow";
import { useWishlist } from "./WishlistStore";
import { wishlistApi } from "./wishlistApi";

export function makeWishlistScreen(): FlowScreen {
  return { id: "wishlist", render: (flow) => <WishlistPage flow={flow} /> };
}

function message(error: unknown) {
  if (error instanceof ProductSearchApiError) {
    if (error.status === 401) return "请从 Beautinow 小程序进入，登录后就能保存和管理心愿。";
    if (error.code === "source_identity_unresolved") return "暂时无法关联这款香水，请按名称搜索。";
    return error.message;
  }
  return error instanceof Error ? error.message : "操作暂时没有完成，请稍后重试";
}

function WishlistPage({ flow }: { flow: FlowControls }) {
  const keyboard = useKeyboard();
  const store = useWishlist();
  const [items, setItems] = useState<WishlistItem[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [query, setQuery] = useState("");
  const [batch, setBatch] = useState<WishlistBatch | null>(null);
  const [candidate, setCandidate] = useState("");
  const [pending, setPending] = useState("");
  const [managing, setManaging] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [allSelected, setAllSelected] = useState(false);
  const [notice, setNotice] = useState("");
  const searchEpoch = useRef(0);

  const load = useCallback(async (nextCursor = "") => {
    if (!nextCursor) setLoading(true);
    setError("");
    try {
      const result = await wishlistApi.list(nextCursor);
      setItems((current) => nextCursor ? [...current, ...result.items] : result.items);
      setCursor(result.nextCursor); setHasMore(result.hasMore);
      store.applyMutation({ outcome: "already_exists", total: result.total, listRevision: result.listRevision, singleDeleteCooldown: result.singleDeleteCooldown });
    } catch (requestError) { setError(message(requestError)); }
    finally { setLoading(false); }
  }, [store.applyMutation]);

  useEffect(() => { void load(); }, [load]);

  async function runSearch(event?: FormEvent) {
    event?.preventDefault();
    const value = query.trim();
    if (!value || pending) return;
    keyboard.hide(); setPending("search"); setError(""); setNotice(""); setCandidate("");
    const epoch = ++searchEpoch.current;
    try {
      const result = await wishlistApi.createSearch(value);
      if (epoch === searchEpoch.current) setBatch(result);
    } catch (requestError) { if (epoch === searchEpoch.current) setError(message(requestError)); }
    finally { if (epoch === searchEpoch.current) setPending(""); }
  }

  async function nextBatch() {
    if (!batch?.batchId || pending) return;
    setPending("next"); setError(""); setCandidate("");
    try { setBatch(await wishlistApi.nextBatch(batch.sessionId, batch.batchId)); }
    catch (requestError) { setError(message(requestError)); }
    finally { setPending(""); }
  }

  async function addCandidate() {
    if (!batch || !candidate || pending) return;
    setPending("add"); setError("");
    try {
      const result = await wishlistApi.addCandidate(batch, candidate);
      store.applyMutation(result);
      setNotice("");
      await load();
      setBatch(null); setCandidate("");
    } catch (requestError) { setError(message(requestError)); }
    finally { setPending(""); }
  }

  async function selectAll() {
    if (allSelected) {
      setSelected(new Set()); setAllSelected(false); setNotice("");
      return;
    }
    try {
      const result = await wishlistApi.selection(store.listRevision);
      setSelected(new Set(result.selectedIds));
      setAllSelected(true);
      if (result.hasMore) setNotice("已选前 50 条，其余请下一次操作");
    } catch (requestError) { setSelected(new Set()); setAllSelected(false); setError(message(requestError)); await load(); }
  }

  async function removeSelected() {
    if (!selected.size || pending) return;
    setPending("batch"); setError("");
    try {
      const result = await wishlistApi.batchDelete([...selected]);
      store.applyMutation(result);
      const removed = new Set(result.removedIds || []);
      setItems((current) => current.filter((item) => !removed.has(item.itemId)));
      setSelected(new Set()); setAllSelected(false); setManaging(false); setNotice("");
    } catch (requestError) { setError(message(requestError)); }
    finally { setPending(""); }
  }

  const selectionLabel = useMemo(() => selected.size ? `删除所选（${selected.size}）` : "请选择心愿", [selected.size]);
  return <div className="wishlist-screen"><main className="wishlist-page" data-testid="wishlist-page">
    <header className="wishlist-header"><button type="button" onClick={flow.pop}>← 返回</button><h1>我的心愿单</h1><button type="button" onClick={() => { setManaging((value) => !value); setSelected(new Set()); setAllSelected(false); }}>{managing ? "完成" : "管理"}</button></header>
    {managing ? <div className="wishlist-manage-bar"><button type="button" onClick={() => void selectAll()}>{allSelected ? "取消全选" : "全选前 50 条"}</button><span>已选 {selected.size} / 50</span></div> : null}
    <div className="wishlist-list-scroll">
      <section className="wishlist-list" aria-label="已加入的香水">
        {loading ? <p className="wishlist-state">正在读取心愿单…</p> : error && !items.length ? <p className="wishlist-state">{error}</p> : !items.length ? <p className="wishlist-state">还没有心愿。<br />在下方输入香水名，找到想要的那一瓶。</p> : items.map((item) => <WishlistItemRow key={item.itemId} item={item} managing={managing} selected={selected.has(item.itemId)} onSelect={() => { setAllSelected(false); setSelected((current) => { const next = new Set(current); if (next.has(item.itemId)) next.delete(item.itemId); else if (next.size < 50) next.add(item.itemId); else setNotice("单次最多选择 50 条"); return next; }); }} />)}
        {hasMore && !managing ? <button className="wishlist-more" type="button" onClick={() => cursor && void load(cursor)}>加载更多 <ArrowRightIcon /></button> : null}
      </section>
      {error && items.length ? <p className="wishlist-error" role="alert">{error}</p> : null}
      {notice ? <p className="wishlist-notice" role="status">{notice}</p> : null}
    </div>
    {managing ? <footer className="wishlist-management-dock"><button className="wishlist-batch-delete" type="button" disabled={!selected.size || Boolean(pending)} onClick={() => void removeSelected()}>{pending === "batch" ? "正在删除…" : selectionLabel}</button></footer> : <footer className="wishlist-search-dock"><form className="wishlist-search" onSubmit={runSearch}>
      <label htmlFor="wishlist-query">想要哪一瓶香水？</label>
      <div><MagnifyingGlassIcon aria-hidden="true" /><KeyboardInput id="wishlist-query" value={query} maxLength={200} placeholder="输入品牌、款式或常用名称" onChange={(event) => setQuery(event.target.value)} onCompositionStart={(event) => { event.currentTarget.dataset.composing = "true"; }} onCompositionEnd={(event) => { delete event.currentTarget.dataset.composing; }} onKeyDown={(event) => { if (event.key === "Enter" && !event.nativeEvent.isComposing && event.currentTarget.dataset.composing !== "true") { event.preventDefault(); void runSearch(); } }} /><button type="submit" disabled={!query.trim() || Boolean(pending)}>{pending === "search" ? "正在找…" : "找香"}</button></div>
      <p className="wishlist-search-help">确认具体款式后加入Beautinow心愿单</p>
    </form></footer>}
    {batch ? <WishlistCandidatePanel batch={batch} selected={candidate} pending={Boolean(pending)} onSelect={setCandidate} onAdd={() => void addCandidate()} onNext={() => void nextBatch()} onClose={() => { if (!pending) { setBatch(null); setCandidate(""); } }} /> : null}
  </main></div>;
}
